import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Bell, Send, Users } from "lucide-react";

/**
 * Admin Push Broadcast — compose a notification and send it to all
 * subscribers (or a specific user id).
 */
export default function AdminPushBroadcast() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/espace-client");
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [subsCount, setSubsCount] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/push/admin/stats")
      .then((r) => setSubsCount(r.data.total || 0))
      .catch(() => setSubsCount(0));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      toast.error("Le message est requis.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/push/admin/broadcast", {
        title: title.trim() || "My Animation",
        body: body.trim(),
        url: url.trim() || "/espace-client",
        user_id: userId.trim() || undefined,
      });
      toast.success(`Notification envoyée à ${data.sent} destinataire(s).`);
      setHistory((h) => [
        { title: title || "My Animation", body, url, sent: data.sent, at: new Date().toLocaleString("fr-FR") },
        ...h.slice(0, 9),
      ]);
      setTitle("");
      setBody("");
      setUserId("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#DCB457] mb-2">Admin · Communication</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl">
          Notifications <span className="italic gold-text">push</span>
        </h1>
        <p className="text-white/60 text-sm mt-3 max-w-lg">
          Envoyez une notification push aux clients qui ont activé les notifications dans leur espace personnel.
        </p>
      </div>

      <div className="card-luxe p-4 mb-6 flex items-center gap-3" data-testid="push-subs-count">
        <Users size={18} className="text-[#DCB457]" />
        <p className="text-sm text-white/85">
          <strong className="text-white">{subsCount ?? "…"}</strong>{" "}
          <span className="text-white/55">abonnement(s) actif(s)</span>
        </p>
      </div>

      <form onSubmit={submit} className="card-luxe p-6 md:p-8 space-y-4" data-testid="push-broadcast-form">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-1.5">Titre</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="🎧 My Animation"
            maxLength={60}
            className="w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2 text-sm outline-none transition"
            data-testid="push-title"
          />
          <p className="text-[10px] text-white/40 mt-1">{title.length}/60</p>
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-1.5">Message *</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            placeholder="Ex : Nouveau créneau disponible en mai — réservez avant qu'il ne parte !"
            maxLength={160}
            rows={3}
            className="w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2 text-sm outline-none transition resize-none"
            data-testid="push-body"
          />
          <p className="text-[10px] text-white/40 mt-1">{body.length}/160</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-1.5">Lien au clic</label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/espace-client"
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2 text-sm outline-none transition"
              data-testid="push-url"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-1.5">
              Cibler un client (optionnel)
            </label>
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="user_id (vide = tous)"
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2 text-sm outline-none transition"
              data-testid="push-user-id"
            />
          </div>
        </div>

        <div className="pt-2 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-white/45">
            Prévisualisez côté client dans <em>Espace client → Profil → Activer les notifications → Envoyer un test</em>.
          </div>
          <button
            type="submit"
            disabled={busy || !body.trim()}
            className="btn-gold inline-flex items-center gap-2 text-sm disabled:opacity-40"
            data-testid="push-broadcast-send"
          >
            <Send size={14} />
            {busy ? "Envoi…" : "Envoyer la notification"}
          </button>
        </div>
      </form>

      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-3">Historique de la session</h2>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="card-luxe p-4 flex items-start gap-3">
                <Bell size={14} className="text-[#DCB457] mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm text-white/90 truncate">{h.title}</p>
                    <span className="text-[10px] text-white/40">{h.at}</span>
                  </div>
                  <p className="text-xs text-white/60 mt-0.5 line-clamp-2">{h.body}</p>
                  <p className="text-[10px] text-[#DCB457] mt-1">
                    envoyée à {h.sent} destinataire(s)
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
